import React, { useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useLocation } from 'react-router-dom';
import { ApplicationContext } from '../../context/GlobalState';
import { extractLocaleFromPath } from '../../i18n';
import SchemaMarkup from '../../components/SchemaMarkup/SchemaMarkup';
import HreflangTags from '../../components/HreflangTags/HreflangTags';
import { buildConditionPageSchema } from '../../utils/schemaMarkup';
import CTABanner from '../../components/CTABanner/CTABanner';
import taxonomy from '../../data/taxonomy.json';
import './ConditionPage.css';

export default function ConditionPage() {
  const { conditionSlug } = useParams();
  const { state } = useContext(ApplicationContext);
  const location = useLocation();
  const { locale } = extractLocaleFromPath(location.pathname);
  const brand = state.brand || state.client;

  const condition = taxonomy.conditionPages?.find(
    (c) => c.slug === conditionSlug
  );

  if (!condition) {
    return (
      <div className="container section-padding">
        <h1>Condition Not Found</h1>
        <p>The condition page you're looking for doesn't exist.</p>
      </div>
    );
  }

  const pageTitle = `${condition.display} in ${brand?.state || 'GA'} | ${brand?.name || 'Georgia Spine & Orthopaedics'}`;
  const metaDescription = `Expert ${condition.display.toLowerCase()} at ${brand?.name}. Board-certified orthopedic specialists. Call today for a free consultation.`;
  const canonicalUrl = `https://${brand?.domain || 'gaspineortho.com'}/${locale}/conditions/${conditionSlug}`;
  const baseDomain = brand?.domain || 'gaspineortho.com';

  // Get related services display names from taxonomy
  const allServices = taxonomy.dimensions?.find((d) => d.id === 'service')?.values || [];
  const relatedServices = (condition.relatedServices || [])
    .map((slug) => allServices.find((s) => s.slug === slug))
    .filter(Boolean);

  const conditionSchema = buildConditionPageSchema({ condition, brand, relatedServices });

  return (
    <div className="condition-page">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        {locale !== 'en' && <html lang={locale} />}
      </Helmet>

      <HreflangTags baseDomain={baseDomain} pathWithoutLocale={`/conditions/${conditionSlug}`} />

      {/* MedicalCondition schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(conditionSchema) }}
      />

      <SchemaMarkup
        type="MedicalWebPage"
        data={{ name: pageTitle, description: metaDescription }}
      />

      {/* Hero */}
      <section className="glass-dark condition-hero" style={{ padding: '80px 0', textAlign: 'center', borderRadius: 0, marginBottom: 0 }}>
        <div className="container">
          <div className="glass-badge" style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', marginBottom: 16, display: 'inline-block', padding: '4px 16px', borderRadius: 20 }}>
            Conditions We Treat
          </div>
          <h1 style={{ fontSize: '2.8rem', fontWeight: 800, color: '#fff', marginBottom: 16 }}>
            {condition.display}
          </h1>
          <p style={{ opacity: 0.9, maxWidth: 620, margin: '0 auto 28px', color: '#fff', fontSize: '1.15rem' }}>
            Expert diagnosis and treatment by board-certified orthopedic specialists and spine surgeons at {brand?.name}.
          </p>
          <a href="/request-appt" className="glass-btn glass-btn-accent" style={{ marginRight: 12 }}>
            Schedule Consultation
          </a>
          <a href={`tel:${brand?.phone?.replace(/\D/g, '')}`} className="glass-btn" style={{ border: '2px solid rgba(255,255,255,0.6)', color: '#fff' }}>
            Call {brand?.phone}
          </a>
        </div>
      </section>

      <div className="container" style={{ maxWidth: 960, margin: '0 auto', padding: '40px 20px 60px' }}>

        {/* Symptoms */}
        {condition.symptoms && condition.symptoms.length > 0 && (
          <div className="glass-panel condition-section" style={{ padding: 32, marginBottom: 24 }}>
            <h2 style={{ marginBottom: 16 }}>Common Symptoms</h2>
            <p style={{ color: 'var(--color-text-secondary)', marginBottom: 16, lineHeight: 1.8 }}>
              Patients with {condition.display.toLowerCase()} commonly experience:
            </p>
            <ul className="condition-symptoms-list">
              {condition.symptoms.map((symptom, i) => (
                <li key={i} style={{ padding: '8px 0', borderBottom: '1px solid var(--glass-border)', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ color: 'var(--color-accent)', fontWeight: 700 }}>✓</span>
                  <span style={{ textTransform: 'capitalize' }}>{symptom}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Treatment Options */}
        {condition.treatmentOptions && condition.treatmentOptions.length > 0 && (
          <div className="glass-panel condition-section" style={{ padding: 32, marginBottom: 24 }}>
            <h2 style={{ marginBottom: 16 }}>Treatment Options</h2>
            <p style={{ color: 'var(--color-text-secondary)', marginBottom: 16, lineHeight: 1.8 }}>
              {brand?.name} offers comprehensive treatment for {condition.display.toLowerCase()}, including:
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
              {condition.treatmentOptions.map((serviceSlug) => {
                const svc = allServices.find((s) => s.slug === serviceSlug);
                if (!svc) return null;
                return (
                  <a
                    key={serviceSlug}
                    href={`/${serviceSlug}`}
                    className="glass-panel"
                    style={{ padding: '14px 18px', textDecoration: 'none', color: 'var(--color-text)', fontSize: '0.95rem', fontWeight: 600, borderRadius: 10, transition: 'transform 0.2s', display: 'block' }}
                  >
                    {svc.display}
                  </a>
                );
              })}
            </div>
          </div>
        )}

        {/* Why Choose Us */}
        <div className="glass-panel condition-section" style={{ padding: 32, marginBottom: 24 }}>
          <h2 style={{ marginBottom: 16 }}>Why Choose {brand?.name}?</h2>
          <p style={{ color: 'var(--color-text-secondary)', lineHeight: 1.8 }}>
            Our team of board-certified orthopedic surgeons, spine specialists, and neurologists provides
            comprehensive, patient-centered care for {condition.display.toLowerCase()}. We use the latest
            diagnostic technology and minimally invasive treatment options to ensure optimal outcomes.
          </p>
          <ul style={{ marginTop: 16, color: 'var(--color-text-secondary)', lineHeight: 2 }}>
            <li>✓ Board-certified orthopedic specialists</li>
            <li>✓ On-site MRI, X-ray, and diagnostic imaging</li>
            <li>✓ Minimally invasive surgical options</li>
            <li>✓ Accepts most insurance, workers' comp &amp; liens</li>
            <li>✓ Multilingual staff — se habla español</li>
          </ul>
        </div>

        {/* Related Services */}
        {relatedServices.length > 0 && (
          <div className="glass-panel condition-section" style={{ padding: 32, marginBottom: 24 }}>
            <h2 style={{ marginBottom: 16 }}>Related Services</h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {relatedServices.map((svc) => (
                <a
                  key={svc.slug}
                  href={`/${svc.slug}`}
                  style={{ padding: '8px 18px', background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', borderRadius: 20, textDecoration: 'none', color: 'var(--color-primary)', fontSize: '0.9rem', fontWeight: 600 }}
                >
                  {svc.display}
                </a>
              ))}
            </div>
          </div>
        )}

      </div>

      <CTABanner client={brand} />
    </div>
  );
}
